import { clsx } from "clsx";
import { type FC } from "react";
import { Tag, TagIntent, TriggerButton, TriggerButtonIntent, TriggerButtonSize } from "widgets";

import { lowerCaseFirstLetter, lowerCaseIfNotAcronym } from "@/app-utils/string.ts";
import { type ListingMapEntry } from "@/graphql.ts";
import {
  GROUP_BY_ATTRIBUTE_CREATION_TIME,
  GROUP_BY_ATTRIBUTE_FEATURES,
  GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
  GROUP_BY_ATTRIBUTE_SPECIES,
  GROUP_BY_ATTRIBUTE_USER_GROUP,
  GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
} from "@/settings/settings.js";

import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import { TooltipController } from "../core/overlays/components/OverlayControllers.js";

export interface IContentBrowserChartBarProps {
  entry: ListingMapEntry;
  proportion: number;
  selected: boolean;
  title?: string;
  selectedContentBrowserFeature: INavPanelCategory;
  selectedContentBrowserAttribute: IRouteNavItemProps;

  onBarClicked: (entry: ListingMapEntry) => void;
  getBarTooltip?: (entry: ListingMapEntry) => string;
  handleEdit?: (entry: ListingMapEntry) => void;
  handleDelete?: (entry: ListingMapEntry) => void;
  editDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
  deleteDisabledTooltip?: (entry: ListingMapEntry) => string; // callback to get the disabled tooltip depending on the entry
}

type translationRules = "name" | "text" | "fixed name" | "genomic text";
const type2Rule = new Map<string, translationRules>();
type2Rule.set(GROUP_BY_ATTRIBUTE_SPECIES, "fixed name");
type2Rule.set(GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE, "text");
type2Rule.set(GROUP_BY_ATTRIBUTE_FEATURES, "text");
type2Rule.set(GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP, "fixed name");
type2Rule.set(GROUP_BY_ATTRIBUTE_USER_GROUP, "fixed name");
type2Rule.set(GROUP_BY_ATTRIBUTE_CREATION_TIME, "fixed name");

/**
 * Row of groupbyAttribute value in the context of the content browser page.
 *
 * created on 5/16/2017
 */
const ContentBrowserChartBar: FC<IContentBrowserChartBarProps> = (props) => {
  const rowClasses = clsx("bar-row", {
    "bar-row-selected": props.selected,
  });

  const barStyle: any = {
    width: props.proportion + "%",
    minWidth: props.proportion > 0 ? "2px" : "0",
    background: "#7494b7",
  };

  // What kind of labels are used for the current featureType+groupByAttribute

  let rule: translationRules = type2Rule.get(props.selectedContentBrowserAttribute.eventKey);

  //Examples:
  // name: Saccharomyces cerevisiae, Cglu_kitasato, ...
  // text: Sequenced Assembly,  Not Sequenced, Re-Sequenced,
  // genomic text:   Genomes with ...,
  //                 Genomes with EC coverage.

  // Label for the tooltip
  // Convert the label to lower case, but not the abbreviations like EC, NGS, NCBI, GeneBank.
  const label: string = lowerCaseIfNotAcronym(props.entry.label);
  if (rule === "text" && (label.includes("genomes") || label.includes("genomic"))) {
    rule = "genomic text";
  }
  let filterText: string;
  switch (rule) {
    case "name":
    case "text":
      filterText = `Show only '${label}' ${lowerCaseFirstLetter(props.selectedContentBrowserFeature.label)}`;
      break;
    case "genomic text":
      filterText = `Show only ${label}`;
      break;
    case "fixed name":
      filterText = `Show only '${props.entry.label}' ${lowerCaseFirstLetter(
        props.selectedContentBrowserFeature.label,
      )}`;
      break;
  }

  const tooltipFromParent: string = props.getBarTooltip ? props.getBarTooltip(props.entry) : undefined;
  const tooltip: string = tooltipFromParent || props.title || (props.selected ? "Remove this filter" : filterText);

  const editDisabledTooltip: string = props.handleEdit ? props.editDisabledTooltip(props.entry) : undefined;
  const deleteDisabledTooltip: string = props.handleDelete ? props.deleteDisabledTooltip(props.entry) : undefined;

  return (
    <TooltipController content={tooltip}>
      {(tooltipTriggerProps) => (
        <div {...tooltipTriggerProps}>
          <div
            onClick={() => {
              props.onBarClicked(props.entry);
            }}
            className={rowClasses}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <div
                className={"bar-action"}
                style={{
                  overflow: "hidden",
                }}
              >
                {props.handleDelete && (
                  <TriggerButton
                    onPress={() => {
                      props.handleDelete?.(props.entry);
                    }}
                    size={TriggerButtonSize.Small}
                    icon={"delete"}
                    isDisabled={deleteDisabledTooltip}
                    label={"Delete"}
                    intent={TriggerButtonIntent.Danger}
                  />
                )}
                <span>&nbsp;</span>
                {props.handleEdit && (
                  <TriggerButton
                    onPress={() => {
                      props.handleEdit?.(props.entry);
                    }}
                    size={TriggerButtonSize.Small}
                    icon={"edit"}
                    isDisabled={editDisabledTooltip}
                    label={"Edit"}
                  />
                )}
                <span>&nbsp;&nbsp;</span>
              </div>
              <div
                className="bar-label-wrapper text-right"
                style={{
                  overflow: "hidden",
                  float: "unset",
                }}
              >
                <span
                  style={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {props.entry.label}&nbsp;&nbsp;
                </span>
                <Tag label={props.entry.value} intentColor={TagIntent.Outlined} />
              </div>
            </div>
            <div className="bar-wrapper" style={{ width: "100%", marginLeft: 20 }}>
              <div style={barStyle} className="bar" />
            </div>
          </div>
        </div>
      )}
    </TooltipController>
  );
};

export { ContentBrowserChartBar };
